'use client';

import { useEffect, useRef } from 'react';

/**
 * Lead tracking data structure for analytics tools like WhatConverts.
 * This data is stored in cookies and DOM elements for reliable capture.
 */
export interface LeadTrackingData {
  cartId: string;
  customer: string | null;
  email: string | null;
  phone: string | null;
  containerSize: string | null;
  orderNumber: string | null;
  orderId: string | null;
  amount: string | null;
  deliveryDate: string | null;
  deliveryLocation: string | null;
  timestamp: string;
}

const COOKIE_NAME = 'trashlab_lead_data';
const DOM_ELEMENT_ID = 'trashlab-lead-data';

/**
 * Parse tracking data from URL search params (base64-encoded JSON)
 */
export function parseTrackingDataFromUrl(): LeadTrackingData | null {
  if (typeof window === 'undefined') return null;

  const params = new URLSearchParams(window.location.search);
  const encoded = params.get('trackingData');

  if (!encoded) return null;

  try {
    const decoded = atob(encoded);
    return JSON.parse(decoded) as LeadTrackingData;
  } catch {
    console.warn('Failed to parse tracking data from URL');
    return null;
  }
}

/**
 * Get tracking data from cookie
 */
export function getFromCookie(): LeadTrackingData | null {
  if (typeof document === 'undefined') return null;

  try {
    const cookies = document.cookie.split(';');
    for (const cookie of cookies) {
      const [name, value] = cookie.trim().split('=');
      if (name === COOKIE_NAME && value) {
        return JSON.parse(decodeURIComponent(value)) as LeadTrackingData;
      }
    }
    return null;
  } catch {
    return null;
  }
}

/**
 * Store tracking data in a cookie.
 * Cookie expires in 1 hour and is set with path=/ for broad accessibility.
 *
 * IMPORTANT: This should be called IMMEDIATELY after checkout completes,
 * BEFORE any page redirect. This eliminates timing issues with analytics tools.
 */
export function storeInCookie(data: LeadTrackingData): void {
  if (typeof document === 'undefined') return;

  try {
    const expires = new Date();
    expires.setTime(expires.getTime() + 60 * 60 * 1000); // 1 hour

    const cookieValue = encodeURIComponent(JSON.stringify(data));
    document.cookie = `${COOKIE_NAME}=${cookieValue}; expires=${expires.toUTCString()}; path=/; SameSite=Lax`;
  } catch {
    console.warn('Failed to store tracking data in cookie');
  }
}

/**
 * Create or update hidden DOM element with tracking data attributes.
 * Analytics tools like WhatConverts can scrape these data attributes.
 *
 * The element is created with id="trashlab-lead-data" and contains
 * all tracking fields as data-* attributes.
 */
export function injectHiddenDomElement(data: LeadTrackingData): void {
  if (typeof document === 'undefined') return;

  // Remove existing element if present
  const existing = document.getElementById(DOM_ELEMENT_ID);
  if (existing) {
    existing.remove();
  }

  // Create new hidden element with data attributes
  const element = document.createElement('div');
  element.id = DOM_ELEMENT_ID;
  element.style.display = 'none';

  // Set data attributes for each tracking field
  element.setAttribute('data-cart-id', data.cartId);
  if (data.customer) element.setAttribute('data-customer', data.customer);
  if (data.email) element.setAttribute('data-email', data.email);
  if (data.phone) element.setAttribute('data-phone', data.phone);
  if (data.containerSize)
    element.setAttribute('data-container-size', data.containerSize);
  if (data.orderNumber)
    element.setAttribute('data-order-number', data.orderNumber);
  if (data.orderId) element.setAttribute('data-order-id', data.orderId);
  if (data.amount) element.setAttribute('data-amount', data.amount);
  if (data.deliveryDate)
    element.setAttribute('data-delivery-date', data.deliveryDate);
  if (data.deliveryLocation)
    element.setAttribute('data-delivery-location', data.deliveryLocation);
  element.setAttribute('data-timestamp', data.timestamp);

  document.body.appendChild(element);
}

/**
 * Update tracking data with order number once it becomes available.
 * Updates both cookie and DOM element.
 */
export function updateTrackingWithOrderNumber(orderNumber: string): void {
  if (typeof window === 'undefined') return;

  const stored = getFromCookie();
  if (stored) {
    stored.orderNumber = orderNumber;
    storeInCookie(stored);
    injectHiddenDomElement(stored);
  }
}

/**
 * Initialize lead tracking - stores in cookie and creates DOM element.
 * Call this IMMEDIATELY after checkout completes, BEFORE any redirect.
 */
export function initializeLeadTracking(data: LeadTrackingData): void {
  storeInCookie(data);
  injectHiddenDomElement(data);
}

interface LeadTrackingInjectorProps {
  /**
   * Order number from polling - when this changes and becomes available,
   * the tracking data will be updated
   */
  orderNumber?: string | null;
}

/**
 * Component that handles lead tracking data injection.
 * Reads tracking data from URL params and stores in cookies and DOM.
 * Also handles updating tracking data when order number becomes available.
 */
export function LeadTrackingInjector({
  orderNumber,
}: LeadTrackingInjectorProps) {
  const hasInitialized = useRef(false);
  const lastOrderNumber = useRef<string | null>(null);

  // Initialize tracking data on mount
  useEffect(() => {
    if (hasInitialized.current) return;

    const data = parseTrackingDataFromUrl();
    if (data) {
      initializeLeadTracking(data);
      hasInitialized.current = true;
    }
  }, []);

  // Update tracking data when order number becomes available
  useEffect(() => {
    if (
      orderNumber &&
      orderNumber !== lastOrderNumber.current &&
      hasInitialized.current
    ) {
      updateTrackingWithOrderNumber(orderNumber);
      lastOrderNumber.current = orderNumber;
    }
  }, [orderNumber]);

  // This component doesn't render anything visible
  return null;
}
